import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { Skeleton, SectionErrorBoundary, VerifiedBadge } from '@shared/ui';
import { captureError } from '@shared/lib/observability';
import PendingBrandHandles from './PendingBrandHandles';
import SkiplistManager from './SkiplistManager';
import BrandDuplicates from './BrandDuplicates';
import DigitalAdsReview from './DigitalAdsReview';

type Tab = 'existing' | 'pending' | 'skipped' | 'duplicates' | 'adcreatives';
type Filter = 'needs_review' | 'no_subcategory' | 'all';
const PAGE_SIZE = 50;

interface BrandRow {
  id: string;
  name: string;
  subcategory_id: string | null;
  needs_review: boolean | null;
  pending_taxonomy: boolean;
  category: string | null;
  gis_primary: string | null; // primary rubric name from gis_rubrics (the clean source)
  ig_handles: { handle: string; role: string | null; verified: boolean | null }[]; // all linked IG accounts, primary first
  claimed: boolean; // a real advertiser has verified ownership of this brand (advertiser_claims)
}

interface SubcatRow {
  id: string;
  name: string;
  name_ru: string | null;
  category_name: string;
  category_name_ru: string | null;
  sort_order: number | null;
  cat_sort_order: number | null;
}

export default function BrandsReview() {
  const { t } = useTranslation('admin');
  const invalidate = useInvalidateSupabase();
  const [params, setParams] = useSearchParams();

  const tab = (params.get('tab') as Tab) || 'existing';
  const filter = (params.get('filter') as Filter) || 'needs_review';
  const q = params.get('q') ?? '';
  const page = parseInt(params.get('page') ?? '0', 10) || 0;

  const setParam = (key: string, val: string) => {
    const next = new URLSearchParams(params);
    if (val) next.set(key, val);
    else next.delete(key);
    if (key !== 'page') next.delete('page'); // reset pagination when filter/search changes
    setParams(next, { replace: true });
  };

  const tabBar = (
    <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid rgba(15,45,100,0.4)' }}>
      {(['existing', 'pending', 'skipped', 'duplicates', 'adcreatives'] as Tab[]).map((k) => (
        <button
          key={k}
          type="button"
          onClick={() => setParam('tab', k)}
          style={{
            padding: '8px 16px', background: 'transparent', border: 'none',
            borderBottom: tab === k ? '2px solid #4a9eff' : '2px solid transparent',
            color: tab === k ? '#fff' : '#9aa',
            fontWeight: tab === k ? 600 : 400, cursor: 'pointer',
            fontFamily: 'inherit', fontSize: 'var(--fs-body)',
          }}
        >
          {k === 'existing' && t('brandsReview.tabExisting', { defaultValue: 'Existing brands' })}
          {k === 'pending' && t('brandsReview.tabPending', { defaultValue: 'Pending IG handles' })}
          {k === 'skipped' && t('brandsReview.tabSkipped', { defaultValue: 'Skipped' })}
          {k === 'duplicates' && t('brandsReview.tabDuplicates', { defaultValue: 'Duplicates' })}
          {k === 'adcreatives' && t('brandsReview.tabAdCreatives', { defaultValue: 'Digital ad creatives' })}
        </button>
      ))}
    </div>
  );

  // Preload all subcategories with parent labels for the dropdown
  // NOTE: hooks must always be called — no early returns before this block
  const subsQ = useSupabaseQuery<SubcatRow[]>(
    ['admin-brands-review-subs'],
    async () => {
      const { data, error } = await supabase
        .from('subcategories')
        .select('id, name, name_ru, sort_order, categories!inner(name, name_ru, sort_order)')
        .eq('is_active', true);
      if (error) return { data: null, error } as any;
      const rows = (data ?? []).map((s: any) => ({
        id: s.id,
        name: s.name,
        name_ru: s.name_ru ?? null,
        sort_order: s.sort_order ?? 999,
        cat_sort_order: s.categories?.sort_order ?? 999,
        category_name: s.categories?.name ?? '',
        category_name_ru: s.categories?.name_ru ?? null,
      })) as SubcatRow[];
      // Sort by category name_ru → subcategory name_ru
      rows.sort((a, b) => {
        const cat = (a.category_name_ru || a.category_name).localeCompare(b.category_name_ru || b.category_name, 'ru');
        if (cat !== 0) return cat;
        return (a.name_ru || a.name).localeCompare(b.name_ru || b.name, 'ru');
      });
      return { data: rows, error: null } as any;
    },
  );
  const subs = subsQ.data ?? [];

  // Fast lookup: subcategory id → Russian label
  const subLabelById = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of subs) {
      const cat = s.category_name_ru || s.category_name;
      const sub = s.name_ru || s.name;
      m.set(s.id, `${cat} › ${sub}`);
    }
    return m;
  }, [subs]);

  const brandsQ = useSupabaseQuery<{ rows: BrandRow[]; total: number }>(
    ['admin-brands-review', filter, q, page],
    async () => {
      let qb = supabase
        .from('brands')
        .select(
          'id, name, subcategory_id, needs_review, pending_taxonomy, category, gis_rubrics, brand_instagram(handle, role, is_verified), advertiser_claims(status)',
          { count: 'exact' },
        );

      if (filter === 'needs_review') qb = qb.eq('needs_review', true);
      else if (filter === 'no_subcategory') qb = qb.is('subcategory_id', null);
      // 'all' = no filter beyond search

      // Match brand NAME or IG HANDLE — users search by "@handle" too. Strip a leading @;
      // handles live on brand_instagram, so resolve matching brand_ids first, then OR them in.
      const term = q.trim().replace(/^@+/, '');
      if (term) {
        const { data: hHits } = await supabase.from('brand_instagram').select('brand_id').ilike('handle', `%${term}%`).limit(100);
        const hIds = [...new Set((hHits ?? []).map((h: { brand_id: string }) => h.brand_id))];
        qb = hIds.length ? qb.or(`name.ilike.%${term}%,id.in.(${hIds.join(',')})`) : qb.ilike('name', `%${term}%`);
      }

      qb = qb.order('name').range(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE - 1);

      const { data, error, count } = await qb;
      if (error) return { data: null, error } as any;
      type RawBrandRow = Omit<BrandRow, 'ig_handles' | 'gis_primary' | 'claimed'> & {
        gis_rubrics?: Array<{ kind?: string | null; name?: string | null }> | null;
        brand_instagram?: Array<{ handle: string; role: string | null; is_verified: boolean | null }> | null;
        advertiser_claims?: Array<{ status: string | null }> | null;
      };
      const rows: BrandRow[] = ((data ?? []) as unknown as RawBrandRow[]).map((b) => {
        const igHandles = (b.brand_instagram ?? [])
          .map((x) => ({ handle: x.handle.replace(/^@+/, ''), role: x.role, verified: x.is_verified }))
          .sort((a, c) => (c.role === 'primary' ? 1 : 0) - (a.role === 'primary' ? 1 : 0));
        const gisPrimary = (b.gis_rubrics ?? []).find((r) => r.kind === 'primary')?.name ?? null;
        const claimed = (b.advertiser_claims ?? []).some((c) => c.status === 'approved' || c.status === 'verified');
        const { brand_instagram: _drop, gis_rubrics: _drop2, advertiser_claims: _drop3, ...rest } = b;
        void _drop; void _drop2; void _drop3;
        return { ...rest, gis_primary: gisPrimary, ig_handles: igHandles, claimed };
      });
      return { data: { rows, total: count ?? 0 }, error: null } as any;
    },
  );
  const rows = brandsQ.data?.rows ?? [];
  const total = brandsQ.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <SectionErrorBoundary name="admin-brands-review">
      <h2 className="admin-page-title">{t('brandsReview.title', { defaultValue: 'Brands review' })}</h2>
      {tabBar}
      {tab === 'pending' && <PendingBrandHandles />}
      {tab === 'skipped' && <SkiplistManager kind="brand" search={q} />}
      {tab === 'duplicates' && <BrandDuplicates />}
      {tab === 'adcreatives' && <DigitalAdsReview />}
      {tab === 'existing' && <><p className="admin-page-sub">
        {t('brandsReview.sub', { defaultValue: 'Brands flagged for taxonomy review or missing subcategory. Pick a subcategory and Save — needs_review clears automatically.' })}
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4 }}>
          {(['needs_review', 'no_subcategory', 'all'] as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setParam('filter', f)}
              className="admin-pill"
              style={{
                cursor: 'pointer',
                background: filter === f ? 'rgba(74,158,255,0.18)' : 'transparent',
                border: '1px solid ' + (filter === f ? '#4a9eff' : 'rgba(15,45,100,0.4)'),
                color: filter === f ? '#4a9eff' : '#aaa',
                padding: '5px 10px', borderRadius: 6, fontSize: 'var(--fs-small)',
              }}
            >
              {f === 'needs_review' && t('brandsReview.filterNeedsReview', { defaultValue: 'Needs review' })}
              {f === 'no_subcategory' && t('brandsReview.filterNoSub', { defaultValue: 'No subcategory' })}
              {f === 'all' && t('brandsReview.filterAll', { defaultValue: 'All' })}
            </button>
          ))}
        </div>
        <input
          type="search"
          placeholder={t('brandsReview.search', { defaultValue: 'Search by name…' })}
          value={q}
          onChange={(e) => setParam('q', e.target.value)}
          style={{
            flex: 1, minWidth: 220, padding: '6px 12px', borderRadius: 6,
            border: '1px solid rgba(15,45,100,0.5)', background: 'rgba(255,255,255,0.04)',
            color: '#eee', fontSize: 'var(--fs-body)', outline: 'none',
          }}
        />
        <span style={{ fontSize: 'var(--fs-small)', color: '#888' }}>
          {brandsQ.isLoading ? '…' : `${total.toLocaleString()} ${t('brandsReview.rows', { defaultValue: 'rows' })}`}
        </span>
      </div>

      {brandsQ.isLoading && <Skeleton height={400} />}
      {brandsQ.error && <div style={{ color: 'var(--danger)' }}>{String((brandsQ.error as Error)?.message ?? brandsQ.error)}</div>}

      {!brandsQ.isLoading && !brandsQ.error && rows.length === 0 && (
        <div className="admin-empty">{t('brandsReview.empty', { defaultValue: 'No brands match the current filter.' })}</div>
      )}

      {!brandsQ.isLoading && rows.length > 0 && (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rows.map((b) => (
              <BrandRowCard
                key={b.id}
                brand={b}
                subs={subs}
                subLabelById={subLabelById}
                onSaved={() => invalidate(['admin-brands-review', filter, q, page])}
              />
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 16 }}>
            <button
              type="button"
              disabled={page === 0}
              onClick={() => setParam('page', String(Math.max(0, page - 1)))}
              className="admin-btn-merge"
              style={{ padding: '6px 14px', fontSize: 'var(--fs-small)' }}
            >
              ← {t('brandsReview.prev', { defaultValue: 'Prev' })}
            </button>
            <span style={{ fontSize: 'var(--fs-small)', color: '#888' }}>
              {page + 1} / {totalPages}
            </span>
            <button
              type="button"
              disabled={page + 1 >= totalPages}
              onClick={() => setParam('page', String(page + 1))}
              className="admin-btn-merge"
              style={{ padding: '6px 14px', fontSize: 'var(--fs-small)' }}
            >
              {t('brandsReview.next', { defaultValue: 'Next' })} →
            </button>
          </div>
        </>
      )}

      <div style={{ marginTop: 16, fontSize: 'var(--fs-caption)', color: '#666' }}>
        <Link to="/admin/health" style={{ color: '#888' }}>← {t('brandsReview.backToHealth', { defaultValue: 'Back to Data Health' })}</Link>
      </div>
      </>}
    </SectionErrorBoundary>
  );
}

interface RowProps {
  brand: BrandRow;
  subs: SubcatRow[];
  subLabelById: Map<string, string>;
  onSaved: () => void;
}

function BrandRowCard({ brand, subs, subLabelById, onSaved }: RowProps) {
  const { t } = useTranslation('admin');
  const [pickedSub, setPickedSub] = useState<string>(brand.subcategory_id ?? '');
  const [busy, setBusy] = useState(false);
  const [savedOk, setSavedOk] = useState(false);

  const save = async () => {
    if (!pickedSub) return;
    setBusy(true);
    setSavedOk(false);
    const { error } = await supabase
      .from('brands')
      .update({ subcategory_id: pickedSub, needs_review: false })
      .eq('id', brand.id);
    setBusy(false);
    if (error) {
      captureError(error, { where: 'admin.brandsReview.save', brandId: brand.id });
      alert(error.message);
      return;
    }
    setSavedOk(true);
    setTimeout(() => onSaved(), 400);
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(180px, 2fr) minmax(220px, 3fr) auto',
      gap: 10,
      padding: '10px 12px',
      borderRadius: 8,
      background: '#1a1d23',
      border: '1px solid #2a2d35',
      alignItems: 'center',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {brand.ig_handles.length > 0 ? (
            <a
              href={`https://www.instagram.com/${brand.ig_handles[0].handle}/`}
              target="_blank"
              rel="noopener noreferrer"
              title={`Open @${brand.ig_handles[0].handle} on Instagram`}
              style={{ color: '#bcd0ff', textDecoration: 'none' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none'; }}
            >
              {brand.name}
            </a>
          ) : (
            <span>{brand.name}</span>
          )}
        </div>
        {brand.ig_handles.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 5 }}>
            {brand.ig_handles.map((h) => {
              const isPrimary = h.role === 'primary';
              return (
                <div key={h.handle} style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 'var(--fs-caption)', minWidth: 0 }}>
                  <a
                    href={`https://www.instagram.com/${h.handle}/`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`Open @${h.handle} on Instagram`}
                    style={{ color: '#e1306c', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'underline'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.textDecoration = 'none'; }}
                  >
                    📷 @{h.handle} ↗
                  </a>
                  <VerifiedBadge verified={h.verified} claimed={brand.claimed} />
                  <span style={{
                    fontSize: 'var(--fs-micro)', fontWeight: 'var(--fw-medium)',
                    padding: '1px 8px', borderRadius: 999, flexShrink: 0,
                    ...(isPrimary
                      ? { background: '#D4537E', color: '#fff' }
                      : { background: 'transparent', color: '#ED93B1', border: '1px solid #D4537E' }),
                  }}>
                    {isPrimary ? 'primary' : (h.role || 'branch')}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        <div style={{ fontSize: 'var(--fs-caption)', color: '#888', display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
          {brand.needs_review && <span style={{ color: '#e6a800' }}>● needs_review</span>}
          {brand.pending_taxonomy && <span style={{ color: '#e6a800' }}>● pending</span>}
          {brand.subcategory_id == null && <span style={{ color: '#e74c3c' }}>● no_subcategory</span>}
          {brand.gis_primary && <span title={brand.gis_primary} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200 }}>rubric: {brand.gis_primary}</span>}
          {brand.category && brand.category !== 'Unknown' && <span style={{ color: '#777' }}>cat-hint: {brand.category}</span>}
        </div>
      </div>

      <select
        value={pickedSub}
        onChange={(e) => { setPickedSub(e.target.value); setSavedOk(false); }}
        className="admin-inline-select"
        style={{ minWidth: 0, width: '100%' }}
      >
        <option value="">— {t('brandsReview.pickSub', { defaultValue: 'pick subcategory' })} —</option>
        {brand.subcategory_id && !subLabelById.has(brand.subcategory_id) && (
          <option value={brand.subcategory_id}>(current) {brand.subcategory_id}</option>
        )}
        {subs.map((s) => (
          <option key={s.id} value={s.id}>
            {(s.category_name_ru || s.category_name)} › {(s.name_ru || s.name)}
          </option>
        ))}
      </select>

      <button
        type="button"
        className="admin-btn-merge"
        disabled={!pickedSub || busy}
        onClick={save}
      >
        {busy ? '…' : savedOk ? '✓' : t('brandsReview.save', { defaultValue: 'Save' })}
      </button>
    </div>
  );
}
